import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Users, Crown, Shield, Star, MessageCircle } from 'lucide-react';
import axios from 'axios';
import Loader from '../components/Loader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roleIcons = {
  fondateur: Crown,
  'co-fondateur': Crown,
  admin: Shield,
  moderateur: Star,
  modérateur: Star,
  helper: Users,
  default: Users
};

const roleColors = {
  fondateur: 'from-yellow-500 to-amber-600',
  'co-fondateur': 'from-yellow-500 to-amber-600',
  developpeur: 'from-indigo-500 to-blue-600',
  admin: 'from-red-500 to-rose-600',
  moderateur: 'from-blue-500 to-cyan-600',
  modérateur: 'from-blue-500 to-cyan-600',
  helper: 'from-green-500 to-emerald-600',
  default: 'from-violet-500 to-purple-600'
};

const Staff = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const response = await axios.get(`${API}/staff`);
        setStaff(response.data);
      } catch (error) {
        console.error('Failed to fetch staff:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStaff();
  }, []);

  const getRoleIcon = (role) => {
    const normalizedRole = role?.toLowerCase() || 'default';
    return roleIcons[normalizedRole] || roleIcons.default;
  };

  const getRoleColor = (role) => {
    const normalizedRole = role?.toLowerCase() || 'default';
    return roleColors[normalizedRole] || roleColors.default;
  };

  const getMemberRoles = (member) => {
    if (Array.isArray(member?.roles) && member.roles.length > 0) {
      return member.roles;
    }
    if (member?.role) {
      return [member.role];
    }
    return [];
  };

  if (loading) {
    return <Loader fullScreen />;
  }

  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Header */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-violet-600/20 mb-6">
              <Users className="w-8 h-8 text-violet-400" />
            </div>
            
            <h1 className="font-heading font-bold text-4xl sm:text-5xl uppercase tracking-wide mb-4">
              <span className="gradient-text">Notre Équipe</span>
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Découvrez les membres qui font vivre notre communauté au quotidien.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Staff Grid */}
      <section className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {staff.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="glass rounded-xl p-12 text-center"
            >
              <Users className="w-16 h-16 text-violet-500/50 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">
                L'équipe sera bientôt présentée ici.
              </p>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {staff.map((member, index) => {
                const roles = getMemberRoles(member);
                const primaryRole = roles[0];
                const RoleIcon = getRoleIcon(primaryRole);
                const roleColor = getRoleColor(primaryRole);
                
                return (
                  <motion.div
                    key={member.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="group glass rounded-xl p-6 hover:border-violet-500/30 transition-all duration-300 card-hover"
                    data-testid={`staff-${index}`}
                  >
                    {/* Avatar */}
                    <div className="relative mb-4">
                      {member.avatar_url ? (
                        <img 
                          src={member.avatar_url}
                          alt={member.name}
                          className="w-24 h-24 rounded-full mx-auto object-cover border-2 border-white/10 group-hover:border-violet-500/50 transition-colors"
                        />
                      ) : (
                        <div className={`w-24 h-24 rounded-full mx-auto bg-gradient-to-br ${roleColor} flex items-center justify-center`}>
                          <span className="text-3xl font-heading font-bold text-white">
                            {member.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      
                      {/* Primary Role Badge */}
                      <div className={`absolute -bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r ${roleColor} text-white text-xs font-heading font-bold uppercase tracking-wider shadow-lg`}>
                        <span className="flex items-center gap-1">
                          <RoleIcon className="w-3 h-3" />
                          {primaryRole}
                        </span>
                      </div>
                    </div>
                    
                    {/* Info */}
                    <div className="text-center mt-6">
                      <h3 className="font-heading font-bold text-lg text-white uppercase tracking-wider">
                        {member.name}
                      </h3>
                      
                      {member.discord_id && (
                        <p className="text-gray-500 text-sm mt-2 flex items-center justify-center gap-1">
                          <MessageCircle className="w-3 h-3" />
                          {member.discord_id}
                        </p>
                      )}

                      {roles.length > 1 && (
                        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                          {roles.slice(1).map((role) => {
                            const SecondaryRoleIcon = getRoleIcon(role);
                            const secondaryRoleColor = getRoleColor(role);

                            return (
                              <span
                                key={`${member.id}-${role}`}
                                className={`inline-flex items-center gap-1 rounded-full bg-gradient-to-r ${secondaryRoleColor} px-3 py-1 text-[11px] font-heading font-bold uppercase tracking-wider text-white`}
                              >
                                <SecondaryRoleIcon className="h-3 w-3" />
                                {role}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Join Staff CTA */}
      <section className="px-4 sm:px-6 lg:px-8 mt-16">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass rounded-xl p-8 text-center relative overflow-hidden"
          >
            {/* Background */}
            <div className="absolute inset-0 bg-gradient-to-r from-violet-600/10 to-blue-600/10" />
            
            <div className="relative">
              <Shield className="w-12 h-12 text-violet-400 mx-auto mb-4" />
              
              <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-wider mb-2">
                Rejoindre l'équipe ?
              </h2>
              <p className="text-gray-400 mb-6 max-w-xl mx-auto">
                Tu souhaites contribuer à la communauté et aider les joueurs ? 
                Postule sur notre Discord !
              </p>
              
              <Link
                to="/join"
                className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white font-heading font-bold uppercase tracking-wider px-8 py-3 rounded-lg transition-all hover:shadow-[0_0_20px_rgba(139,92,246,0.5)]"
                data-testid="join-staff-btn"
              >
                <MessageCircle className="w-5 h-5" />
                Postuler sur Discord
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Staff;
